import React, { useState, useRef } from 'react';
import cx from 'classnames';
import { withSize } from 'react-sizeme';
import MouseTooltip from 'react-sticky-mouse-tooltip';
import { Text } from '../../../../../ui-kit';
import mediaSizes from '../../../../../common/constants/mediaSizes';
import '../styles/_comments-overlay-button.scss';

export const TOOLTIP_TEXT = 'Click or drag to select an area to leave a comment';

// Minimum pointer travel (px) before a press is treated as a box-draw
// rather than a click. Keeps small accidental drags behaving as dots.
const DRAG_THRESHOLD = 6;




function CommentsOverlayButton({
  shouldShowTooltip,
  onClick,
  onDrawBox,
  size: { width }
}) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [preview, setPreview] = useState(null);

  // Refs so the window listeners always read the latest drag state.
  const startRef = useRef(null);
  const didDragRef = useRef(false);
  const overlayRectRef = useRef(null);

  const onMouseOver = ({ target, currentTarget }) => {
    const isOverlay = target && currentTarget && target === currentTarget;

    if (tooltipVisible || !isOverlay || !shouldShowTooltip) {
      return;
    }
    setTooltipVisible(true);
  };

  const onMouseOut = ({ target, currentTarget }) => {
    const isOverlay = target && currentTarget && target === currentTarget;

    if (!isOverlay || !tooltipVisible) {
      return;
    }
    setTooltipVisible(false);
  };

  const handleWindowMouseMove = (event) => {
    const start = startRef.current;
    if (!start) {
      return;
    }

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    if (!didDragRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) {
      return;
    }

    didDragRef.current = true;
    setTooltipVisible(false);

    const rect = overlayRectRef.current;
    if (rect) {
      // Position the live preview relative to the overlay (its offset parent).
      setPreview({
        left: Math.min(start.x, event.clientX) - rect.left,
        top: Math.min(start.y, event.clientY) - rect.top,
        width: Math.abs(event.clientX - start.x),
        height: Math.abs(event.clientY - start.y)
      });
    }
  };

  const handleWindowMouseUp = (event) => {
    window.removeEventListener('mousemove', handleWindowMouseMove);
    window.removeEventListener('mouseup', handleWindowMouseUp);

    const start = startRef.current;
    startRef.current = null;
    setPreview(null);

    if (didDragRef.current && start && typeof onDrawBox === 'function') {
      onDrawBox({
        startX: start.x,
        startY: start.y,
        endX: event.clientX,
        endY: event.clientY
      });
    }
    // didDragRef stays true here so the synthetic click that follows a drag
    // is suppressed in handleClick; it is reset there.
  };

  const handleMouseDown = (event) => {
    // Only respond to the primary (left) button.
    if (event.button !== 0) {
      return;
    }

    didDragRef.current = false;
    startRef.current = { x: event.clientX, y: event.clientY };
    overlayRectRef.current = event.currentTarget.getBoundingClientRect();

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
  };

  const handleClick = (event) => {
    // A drag just finished — swallow the trailing click so it doesn't drop a dot.
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    onClick(event);
  };

  const supportsTooltip = width > mediaSizes.small;

  const buttonMouseListeners = {
    onMouseOver,
    onMouseOut,
    onMouseUp: onMouseOver,
    onBlur: onMouseOut
  };

  return (
    <>
      {tooltipVisible && supportsTooltip &&
      <MouseTooltip offsetX={15} className="comments-overlay-tooltip">
          <Text noMargin darkTheme label>
            {TOOLTIP_TEXT}
          </Text>
        </MouseTooltip>}

      {preview &&
      <div
        className="comments-overlay-button__preview"
        style={{
          left: `${preview.left}px`,
          top: `${preview.top}px`,
          width: `${preview.width}px`,
          height: `${preview.height}px`
        }} />}

      <button
        type="button"
        className={cx(
          'comments-overlay-button',
          (tooltipVisible || preview) && 'comments-overlay-button__crosshair'
        )}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        {...supportsTooltip ? { ...buttonMouseListeners } : {}} />

    </>);

}

export default withSize()(CommentsOverlayButton);
